export const AIRTABLE_BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID;
const AIRTABLE_TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;
const DEFAULT_TABLE_ID = process.env.REACT_APP_AIRTABLE_TABLE_ID;

const API_ROOT = AIRTABLE_BASE_ID
  ? `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}`
  : null;
const CONTENT_API_ROOT = AIRTABLE_BASE_ID
  ? `https://content.airtable.com/v0/${AIRTABLE_BASE_ID}`
  : null;

export const AIRTABLE_TABLES = {
  solicitudes: process.env.REACT_APP_AIRTABLE_SOLICITUDES_TABLE_ID || DEFAULT_TABLE_ID,
  clientes: process.env.REACT_APP_AIRTABLE_CLIENTES_TABLE_ID,
  actividades: process.env.REACT_APP_AIRTABLE_ACTIVIDADES_TABLE_ID
};

function buildUrl(tableId) {
  const resolvedTable = tableId || DEFAULT_TABLE_ID;
  if (!API_ROOT || !resolvedTable) return null;
  return `${API_ROOT}/${resolvedTable}`;
}

function buildQueryUrl(tableId, params = {}) {
  const endpoint = buildUrl(tableId);
  if (!endpoint) return null;

  const url = new URL(endpoint);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item === undefined || item === null || item === '') return;
        url.searchParams.append(key, item);
      });
      return;
    }
    url.searchParams.append(key, value);
  });
  return url.toString();
}

export async function fetchAirtableRecords({
  tableId,
  maxRecords = 20,
  view,
  filterByFormula,
  fields,
  returnFieldsByFieldId,
  sort
} = {}) {
  if (!AIRTABLE_TOKEN) {
    throw new Error('Falta REACT_APP_AIRTABLE_TOKEN en variables de entorno');
  }

  const params = {
    maxRecords,
    view,
    filterByFormula,
    returnFieldsByFieldId,
    'fields[]': fields
  };

  if (Array.isArray(sort)) {
    sort.forEach((item, index) => {
      if (!item || !item.field) return;
      params[`sort[${index}][field]`] = item.field;
      if (item.direction) {
        params[`sort[${index}][direction]`] = item.direction;
      }
    });
  }

  const endpoint = buildQueryUrl(tableId, params);
  if (!endpoint) {
    throw new Error('Define REACT_APP_AIRTABLE_BASE_ID y un ID de tabla válido');
  }

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${AIRTABLE_TOKEN}`,
    },
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Airtable error ${response.status}: ${message}`);
  }

  const data = await response.json();
  return data.records || [];
}

export async function fetchAllAirtableRecords({
  tableId,
  maxRecords = 1000,
  view,
  filterByFormula,
  pageSize = 100,
  fields,
  returnFieldsByFieldId,
  sort
} = {}) {
  if (!AIRTABLE_TOKEN) {
    throw new Error('Falta REACT_APP_AIRTABLE_TOKEN en variables de entorno');
  }

  const resolvedTable = tableId || DEFAULT_TABLE_ID;
  if (!API_ROOT || !resolvedTable) {
    throw new Error('Define REACT_APP_AIRTABLE_BASE_ID y un ID de tabla válido');
  }

  let records = [];
  let offset;
  do {
    const limit = Math.min(pageSize, maxRecords - records.length);
    const params = {
      pageSize: limit,
      view,
      filterByFormula,
      offset,
      'fields[]': fields,
      returnFieldsByFieldId
    };
    if (Array.isArray(sort)) {
      sort.forEach((item, index) => {
        if (!item || !item.field) return;
        params[`sort[${index}][field]`] = item.field;
        if (item.direction) {
          params[`sort[${index}][direction]`] = item.direction;
        }
      });
    }
    const endpoint = buildQueryUrl(resolvedTable, params);
    if (!endpoint) break;

    const response = await fetch(endpoint, {
      headers: {
        Authorization: `Bearer ${AIRTABLE_TOKEN}`,
      },
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Airtable error ${response.status}: ${message}`);
    }

    const data = await response.json();
    records = records.concat(data.records || []);
    offset = data.offset;
  } while (offset && records.length < maxRecords);

  return records;
}

export async function createAirtableRecord({ tableId, fields }) {
  if (!AIRTABLE_TOKEN) {
    throw new Error('Falta REACT_APP_AIRTABLE_TOKEN en variables de entorno');
  }

  const endpoint = buildUrl(tableId);
  if (!endpoint) {
    throw new Error('Define REACT_APP_AIRTABLE_BASE_ID y un ID de tabla válido');
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${AIRTABLE_TOKEN}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ fields })
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Airtable error ${response.status}: ${message}`);
  }

  return response.json();
}

export async function updateAirtableRecord({ tableId, recordId, fields, typecast = false }) {
  if (!AIRTABLE_TOKEN) {
    throw new Error('Falta REACT_APP_AIRTABLE_TOKEN en variables de entorno');
  }
  if (!recordId) {
    throw new Error('Se requiere el ID del registro de Airtable para actualizarlo');
  }

  const endpoint = buildUrl(tableId);
  if (!endpoint) {
    throw new Error('Define REACT_APP_AIRTABLE_BASE_ID y un ID de tabla válido');
  }

  const body = {
    records: [
      {
        id: recordId,
        fields
      }
    ],
    typecast
  };

  const response = await fetch(endpoint, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${AIRTABLE_TOKEN}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Airtable error ${response.status}: ${message}`);
  }

  const data = await response.json();
  return (data.records && data.records[0]) || null;
}

export async function deleteAirtableRecord({ tableId, recordId }) {
  if (!AIRTABLE_TOKEN) {
    throw new Error('Falta REACT_APP_AIRTABLE_TOKEN en variables de entorno');
  }
  if (!recordId) {
    throw new Error('Se requiere el ID del registro de Airtable para eliminarlo');
  }

  const endpoint = buildUrl(tableId);
  if (!endpoint) {
    throw new Error('Define REACT_APP_AIRTABLE_BASE_ID y un ID de tabla válido');
  }

  const url = new URL(endpoint);
  url.searchParams.append('records[]', recordId);

  const response = await fetch(url.toString(), {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${AIRTABLE_TOKEN}`,
    },
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Airtable error ${response.status}: ${message}`);
  }

  const data = await response.json();
  const first = Array.isArray(data?.records) ? data.records[0] : null;
  return Boolean(first?.deleted);
}

export async function fetchAirtableSchema() {
  if (!AIRTABLE_TOKEN) {
    throw new Error('Falta REACT_APP_AIRTABLE_TOKEN en variables de entorno');
  }
  if (!AIRTABLE_BASE_ID) {
    throw new Error('Define REACT_APP_AIRTABLE_BASE_ID para consultar el esquema');
  }

  const response = await fetch(`https://api.airtable.com/v0/meta/bases/${AIRTABLE_BASE_ID}/tables`, {
    headers: {
      Authorization: `Bearer ${AIRTABLE_TOKEN}`
    }
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Airtable schema error ${response.status}: ${message}`);
  }

  return response.json();
}

function fileToBase64(file) {
  if (!file) return Promise.resolve(null);
  if (typeof FileReader === 'undefined') {
    return Promise.reject(new Error('La carga de adjuntos no es compatible con este navegador.'));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('No se pudo leer el archivo seleccionado.'));
        return;
      }
      const commaIndex = result.indexOf(',');
      const base64 = commaIndex !== -1 ? result.slice(commaIndex + 1) : result;
      resolve(base64);
    };
    reader.onerror = () => {
      reject(new Error('Error al leer el archivo seleccionado.'));
    };
    reader.readAsDataURL(file);
  });
}

export async function uploadRecordAttachment({ recordId, fieldId, file }) {
  if (!file) return;
  if (!AIRTABLE_TOKEN) {
    throw new Error('Falta REACT_APP_AIRTABLE_TOKEN en variables de entorno');
  }
  if (!CONTENT_API_ROOT || !AIRTABLE_BASE_ID) {
    throw new Error('Define REACT_APP_AIRTABLE_BASE_ID para subir adjuntos');
  }
  if (!recordId || !fieldId) {
    throw new Error('Faltan identificadores de registro o campo para adjuntar archivos');
  }

  const endpoint = `${CONTENT_API_ROOT}/${recordId}/${fieldId}/uploadAttachment`;
  const base64 = await fileToBase64(file);

  const body = {
    contentType: file.type || 'application/octet-stream',
    file: base64,
    filename: file.name || 'archivo'
  };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${AIRTABLE_TOKEN}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Airtable upload error ${response.status}: ${message}`);
  }

  return response.json();
}

export function getSolicitudesTableId() {
  return AIRTABLE_TABLES.solicitudes || DEFAULT_TABLE_ID;
}

export async function fetchInvoiceRequests(options = {}) {
  const tableId = options.tableId || getSolicitudesTableId();
  return fetchAirtableRecords({
    tableId,
    maxRecords: options.maxRecords,
    // Do not force a specific Airtable view by default because
    // view-level filters can hide records unexpectedly.
    view: options.view,
    filterByFormula: options.filterByFormula
  });
}

export async function createInvoiceRequest(fields, options = {}) {
  const tableId = options.tableId || getSolicitudesTableId();
  return createAirtableRecord({ tableId, fields });
}
